/**
 * Shift-select range and Ctrl/Cmd-click toggle helpers for the buyer-board
 * line table. Ranges follow the table's visible row order (sort + filters),
 * matching GAINS shift-highlight.
 */

/** Inclusive range of ids between anchor and target in `orderedIds`. */
export function lineIdsInShiftRange(
  orderedIds: readonly string[],
  anchorId: string,
  targetId: string,
): string[] {
  const anchorIndex = orderedIds.indexOf(anchorId);
  const targetIndex = orderedIds.indexOf(targetId);
  if (anchorIndex < 0 || targetIndex < 0) return [];
  const start = Math.min(anchorIndex, targetIndex);
  const end = Math.max(anchorIndex, targetIndex);
  return orderedIds.slice(start, end + 1);
}

/** `current` with `id` added, or removed when it is already in. */
export function toggleLineId(
  current: ReadonlySet<string>,
  id: string,
): Set<string> {
  const next = new Set(current);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

/**
 * The lines the buyer sees as selected: the multi-line highlight, or else the
 * one line open in the Line section. A highlight of one line is the line a
 * plain click left behind, and stops counting once the Line section moves on,
 * unless the open line is filtered out of the table: then the highlight is
 * what survived pruning a multi-line selection, and it stays selected.
 */
export function shownLineSelection(
  highlightedIds: ReadonlySet<string>,
  selectedLineId: string | undefined,
  visibleIds: ReadonlySet<string>,
): ReadonlySet<string> {
  if (highlightedIds.size > 1) return highlightedIds;
  if (selectedLineId === undefined) return new Set();
  if (!visibleIds.has(selectedLineId)) return highlightedIds;
  return new Set([selectedLineId]);
}

/**
 * The line the Line section shows after a Ctrl/Cmd-click toggled `toggledId`:
 * the toggled line when it went in; the sole line left, since a one-line
 * selection is the open line (`shownLineSelection`); when the shown line went
 * out, the most recently added line still selected (null clears the section).
 */
export function lineAfterToggle(
  next: ReadonlySet<string>,
  toggledId: string,
  selectedLineId: string | undefined,
): string | null {
  if (next.has(toggledId)) return toggledId;
  if (next.size <= 1) return [...next][0] ?? null;
  if (toggledId !== selectedLineId) return selectedLineId ?? null;
  return [...next].at(-1) ?? null;
}

/** Lines to update when the buyer edits a cell on `originId`. */
export function bulkEditTargetIds(
  highlightedIds: ReadonlySet<string>,
  originId: string,
): readonly string[] {
  if (highlightedIds.size > 1 && highlightedIds.has(originId)) {
    return [...highlightedIds];
  }
  return [originId];
}

/** Drop highlights for lines that left the visible set. */
export function pruneHighlightedIds(
  highlightedIds: ReadonlySet<string>,
  visibleIds: ReadonlySet<string>,
): Set<string> {
  if (highlightedIds.size === 0) return new Set();
  const next = new Set<string>();
  for (const id of highlightedIds) {
    if (visibleIds.has(id)) next.add(id);
  }
  return next;
}
